import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { CsvDownloadButton } from '@/components/venue/CsvDownloadButton'
import { ReservationRefundResultListTable } from '@/components/venue/ReservationRefundResultListTable'
import { useVenueReservationRefunds } from '@/hooks/useVenueReservationRefunds'
import { todayDateKey } from '@/lib/venue/calendarGrid'
import { downloadReservationRefundsCsv } from '@/lib/venue/reservationRefundApi'
import type { ReservationRefundListStatus, ReservationRefundMethodFilter } from '@/lib/venue/reservationRefundTypes'
import {
  REFUND_METHOD_OPTIONS,
  REFUND_STATUS_OPTIONS,
  defaultRefundListFilter,
  validateRefundListPeriod,
  type ReservationRefundListFilter,
} from '@/lib/venue/reservationRefundViewModel'
import { useVenueContext } from '@/routes/venueContext'

const REFLECTED_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'true', label: '반영됨' },
  { value: 'false', label: '미반영' },
]

/** 환불 처리 결과 — 'sales/refunds'(설계 §2). 상태·유형·정산 반영 여부·기간 필터 + CSV 다운로드. */
export function VenueSalesRefundsPage() {
  const { venueId } = useVenueContext()

  const [appliedFilter, setAppliedFilter] = useState<ReservationRefundListFilter>(() =>
    defaultRefundListFilter(todayDateKey())
  )
  const [dateInputs, setDateInputs] = useState({ from: appliedFilter.from, to: appliedFilter.to })

  const refundsQuery = useVenueReservationRefunds(venueId, appliedFilter)
  const periodError = validateRefundListPeriod(dateInputs)

  const pages = refundsQuery.data?.pages ?? []
  const items = pages.flatMap((page) => page.refunds)

  function applyDateRange() {
    if (periodError) return
    setAppliedFilter((current) => ({ ...current, from: dateInputs.from, to: dateInputs.to }))
  }

  function toggleStatus(status: ReservationRefundListStatus) {
    setAppliedFilter((current) => ({
      ...current,
      statuses: current.statuses.includes(status)
        ? current.statuses.filter((value) => value !== status)
        : [...current.statuses, status],
    }))
  }

  function toggleMethod(method: ReservationRefundMethodFilter) {
    setAppliedFilter((current) => ({
      ...current,
      methods: current.methods.includes(method)
        ? current.methods.filter((value) => value !== method)
        : [...current.methods, method],
    }))
  }

  function changeReflected(value: string) {
    setAppliedFilter((current) => ({
      ...current,
      settlementReflected: value === 'ALL' ? undefined : value === 'true',
    }))
  }

  return (
    <>
      <PageHeader
        title="환불 처리 결과"
        description="처리 상태·환불 유형·정산 반영 여부로 환불 내역을 조회합니다."
        action={
          <CsvDownloadButton
            onDownload={() => downloadReservationRefundsCsv(venueId, appliedFilter)}
          />
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-refunds-from">
            시작일
          </label>
          <Input
            id="sales-refunds-from"
            type="date"
            value={dateInputs.from}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, from: event.target.value }))
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-refunds-to">
            종료일
          </label>
          <Input
            id="sales-refunds-to"
            type="date"
            value={dateInputs.to}
            onChange={(event) => {
              setDateInputs((current) => ({ ...current, to: event.target.value }))
            }}
          />
        </div>
        <div className="w-40">
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-refunds-reflected">
            정산 반영 여부
          </label>
          <Select
            id="sales-refunds-reflected"
            value={
              appliedFilter.settlementReflected === undefined
                ? 'ALL'
                : String(appliedFilter.settlementReflected)
            }
            onChange={(event) => {
              changeReflected(event.target.value)
            }}
          >
            {REFLECTED_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <Button size="md" disabled={Boolean(periodError)} onClick={applyDateRange}>
          조회
        </Button>
      </div>

      {periodError ? <p className="mb-3 text-xs text-danger-500">{periodError}</p> : null}

      <div role="group" aria-label="처리 상태 필터" className="mb-3 flex flex-wrap gap-2">
        {REFUND_STATUS_OPTIONS.map((option) => {
          const selected = appliedFilter.statuses.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                toggleStatus(option.value)
              }}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      <div role="group" aria-label="환불 유형 필터" className="mb-4 flex flex-wrap gap-2">
        {REFUND_METHOD_OPTIONS.map((option) => {
          const selected = appliedFilter.methods.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                toggleMethod(option.value)
              }}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {refundsQuery.isLoading ? <LoadingState rows={4} label="환불 처리 결과를 불러오는 중" /> : null}

      {refundsQuery.isError ? (
        <ErrorState
          error={refundsQuery.error}
          onRetry={() => {
            void refundsQuery.refetch()
          }}
        />
      ) : null}

      {!refundsQuery.isLoading && !refundsQuery.isError ? (
        <ReservationRefundResultListTable
          items={items}
          hasNextPage={refundsQuery.hasNextPage ?? false}
          isFetchingNextPage={refundsQuery.isFetchingNextPage}
          onLoadMore={() => {
            void refundsQuery.fetchNextPage()
          }}
        />
      ) : null}
    </>
  )
}
